import ButtonOutline from 'components/common/button/outline';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
    usePostApiV1ClientIdentitySendOtpMutation,
    usePostApiV1ClientIdentityVerifyOtpMutation,
    usePostApiV1ClientUserFavoritesAddUserFavoritesMutation,
} from 'service/api/allApis';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { selectBookmarks } from 'store/features/bookmarksSlice';
import { PhoneInput } from 'components/common/phone-input';
import { PHONE_NUMBER_REGEX } from 'lib/constants';
import { useRef, useState } from 'react';
import { RenderIf } from 'components/common/render-if';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSeparator,
    InputOTPSlot,
} from 'components/common/otp-input';

interface LoginProps {
    onRegisterOpen: () => void;
    onLoginClose: () => void;
}

type FormValues = {
    phoneNumber: string;
    otpCode?: string;
};

enum STEP {
    SEND_OTP,
    VERIFY_OTP,
}

const getSchema = (t: TFunction<'translation', undefined>, step: STEP) =>
    yup
        .object({
            phoneNumber: yup
                .string()
                .required(t('validation.field-required', { field: t('auth.phone-number') }))
                .matches(
                    PHONE_NUMBER_REGEX,
                    t('validation.invalid-field', { field: t('auth.phone-number') }),
                ),
            otpCode:
                step === STEP.VERIFY_OTP
                    ? yup
                          .string()
                          .required(t('validation.field-required', { field: t('auth.otp') }))
                          .min(4, t('validation.invalid-field', { field: t('auth.otp') }))
                          .max(4, t('validation.invalid-field', { field: t('auth.otp') }))
                    : yup.string(),
        })
        .required();

const Login = ({ onRegisterOpen, onLoginClose }: LoginProps) => {
    const [sendOtpMutation, { isLoading: sendOtpLoading }] =
        usePostApiV1ClientIdentitySendOtpMutation();
    const [verifyOtpMutation, { isLoading: verifyOtpLoading }] =
        usePostApiV1ClientIdentityVerifyOtpMutation();
    const isLoading = sendOtpLoading || verifyOtpLoading;
    const [addUserFavoritesMutation] = usePostApiV1ClientUserFavoritesAddUserFavoritesMutation();
    const { bookmarks } = useSelector(selectBookmarks);
    const { t } = useTranslation();
    const [step, setStep] = useState(STEP.SEND_OTP);
    const {
        watch,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(getSchema(t, step)),
    });
    const otpInputRef = useRef<HTMLInputElement>(null);

    const onSubmit: SubmitHandler<FormValues> = (values) => {
        if (step === STEP.SEND_OTP) {
            sendOtpMutation({
                phoneNumber: values.phoneNumber,
            })
                .unwrap()
                .then(() => {
                    toast.success(t('auth.otp-sent'));
                    setStep(STEP.VERIFY_OTP);
                    setTimeout(() => {
                        otpInputRef.current?.focus();
                    }, 500);
                })
                .catch((e) => {
                    const errorMessage =
                        e.data?.message ?? e?.data?.Title ?? t('general.something-went-wrong');
                    toast.error(errorMessage);
                });
        } else {
            verifyOtpMutation({
                otpCode: Number(values.otpCode),
                phoneNumber: values.phoneNumber,
            })
                .unwrap()
                .then(() => {
                    toast.success(t('auth.login-success'));
                    if (bookmarks.length > 0)
                        addUserFavoritesMutation({
                            body: bookmarks,
                        });
                    onLoginClose();
                })
                .catch((e) => {
                    const errorMessage =
                        e.data?.message ?? e?.data?.Title ?? t('general.something-went-wrong');
                    toast.error(errorMessage);
                });
        }
    };

    return (
        <div className="w-[352px] md:min-h-[360px] lg:min-h-[400px] flex flex-col items-center justify-center border border-dark-20 py-[30px] px-10">
            <h2 className="text-[32px] font-bold text-txt">{t('auth.welcome')}</h2>
            <RenderIf condition={step === STEP.SEND_OTP}>
                <div className="mt-5 mb-8 flex items-center gap-x-3">
                    <p className="text-sm text-txt-60 font-medium">
                        {t('auth.dont-have-an-account')}
                    </p>
                    <button
                        onClick={onRegisterOpen}
                        className=" text-brend text-sm font-semibold underline hover:text-primary cursor-pointer"
                    >
                        {t('auth.register')}
                    </button>
                </div>
            </RenderIf>
            <RenderIf condition={step === STEP.VERIFY_OTP}>
                <div className="mt-5 mb-8 ">
                    <p className="text-xs text-txt-60 font-medium">
                        {t('auth.otp-sent-to', {
                            phone: watch('phoneNumber'),
                        })}
                        .
                        <button
                            onClick={() => setStep(STEP.SEND_OTP)}
                            className=" text-brend text-xs font-semibold underline hover:text-primary cursor-pointer ml-1"
                        >
                            {t('general.edit')}
                        </button>
                    </p>
                </div>
            </RenderIf>
            <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col gap-y-8 pb-6">
                <RenderIf condition={step === STEP.SEND_OTP}>
                    <div className="py-6">
                        <PhoneInput
                            defaultCountry="AZ"
                            international
                            placeholder="Enter a phone number"
                            onChange={(value) => setValue('phoneNumber', value)}
                        />
                        {errors.phoneNumber && (
                            <p className="text-red-600 text-xs h-4 mt-2">
                                {errors.phoneNumber.message}
                            </p>
                        )}
                    </div>
                </RenderIf>
                <RenderIf condition={step === STEP.VERIFY_OTP}>
                    <InputOTP
                        maxLength={4}
                        ref={otpInputRef}
                        className="mx-auto"
                        onChange={(value) => setValue('otpCode', value)}
                    >
                        <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                        </InputOTPGroup>
                    </InputOTP>
                </RenderIf>
                <ButtonOutline
                    loading={isLoading}
                    text={step === STEP.SEND_OTP ? t('auth.login') : t('auth.verify')}
                    className="rounded-sm text-txt-60 border border-txt-60"
                />
            </form>
        </div>
    );
};

export default Login;
